/**
 * EmberField — cut-safe ambient garnish for the HUD bridge (Wave 2, Item 10).
 *
 * A purely decorative particle layer whose ember count, velocity, and drift
 * direction are driven EXCLUSIVELY by the live FleetMood signal:
 *
 *   idle     → 4 banked coals drifting UP at low velocity
 *   building → 20 embers drifting UP at high velocity (count never exceeds 20)
 *   halted   → embers tinted house-red (var(--color-blocker)) drifting DOWN
 *              — rising = good, falling = bad is the semantic contract
 *   healing / waiting / down → quiet upward variants (healing=6, waiting=4,
 *              down=3 — garnish density only, never a signal of its own)
 *
 * Hard rules (binding on any edit):
 *  - transform + opacity keyframes ONLY — no top/left/width/height or any
 *    layout-affecting property inside an animation. Static placement (top/left
 *    percentages set once per ember) is not animation and is allowed.
 *  - Every ember is absolutely positioned inside a relatively-positioned
 *    container so particle state changes never disturb layout.
 *  - Hidden below lg (`hidden lg:block`) — never renders/animates on sm/md.
 *  - prefers-reduced-motion → render NULL (zero DOM nodes). The reduced-motion
 *    read happens SYNCHRONOUSLY in the useState initializer, before any DOM or
 *    keyframe is emitted, so there is no flash-of-motion before an early return.
 *  - House tokens only: ember body is var(--color-brand); halted tint is
 *    var(--color-blocker) (the ONE house red). No raw hex/rgb literals.
 *
 * The board (BridgeShell) is fully functional when EmberField returns null —
 * it is a sibling of the static grid/vignette in the atmosphere layer and
 * owns no state the board needs.
 */
import React, { useEffect, useState } from 'react';
import type { FleetMood } from '../fleet/fleetMood';

/** Hard ceiling — the acceptance contract caps the field at 20 embers. */
const MAX_EMBERS = 20;

interface MoodField {
  count: number;
  /** Animation variant: slow/fast upward drift, or halted downward fall. */
  drift: 'up' | 'up-fast' | 'down';
}

const MOOD_FIELD: Record<FleetMood, MoodField> = {
  idle: { count: 4, drift: 'up' },
  building: { count: 20, drift: 'up-fast' },
  halted: { count: 10, drift: 'down' },
  healing: { count: 6, drift: 'up' },
  waiting: { count: 4, drift: 'up' },
  down: { count: 3, drift: 'up' },
};

/**
 * Reduced-motion guard. The initial read is SYNCHRONOUS (useState lazy
 * initializer) so the very first render already knows the answer — no frame
 * of animation can escape before the null return. A listener keeps it honest
 * if the OS preference flips while the bridge is mounted.
 */
function useReducedMotionPref(): boolean {
  const [reduced, setReduced] = useState<boolean>(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    return undefined;
  }, []);
  return reduced;
}

/**
 * Keyframes — transform + opacity ONLY. Up variants translate to NEGATIVE Y
 * (rise = good); the halted variant translates to POSITIVE Y (fall = bad).
 * Emitted only when the field renders, so a reduced-motion null leaves zero
 * keyframes in the DOM.
 */
const EMBER_KEYFRAMES = `
@keyframes ef-drift-up {
  0%   { transform: translateY(0);      opacity: 0; }
  12%  { opacity: 0.85; }
  100% { transform: translateY(-160px); opacity: 0; }
}
@keyframes ef-drift-up-fast {
  0%   { transform: translateY(0);      opacity: 0; }
  10%  { opacity: 1; }
  100% { transform: translateY(-260px); opacity: 0; }
}
@keyframes ef-drift-down {
  0%   { transform: translateY(0);     opacity: 0; }
  12%  { opacity: 0.9; }
  100% { transform: translateY(200px); opacity: 0; }
}
`;

const DRIFT_CLASS: Record<MoodField['drift'], string> = {
  up: 'ef-ember--up',
  'up-fast': 'ef-ember--up-fast',
  down: 'ef-ember--down',
};

const DRIFT_ANIMATION: Record<MoodField['drift'], string> = {
  up: 'ef-drift-up',
  'up-fast': 'ef-drift-up-fast',
  down: 'ef-drift-down',
};

/** Deterministic per-ember placement + timing (stable across renders/tests). */
function emberStyle(index: number, field: MoodField): React.CSSProperties {
  const leftPct = (index * 53 + 11) % 96; // spread across the width
  // Up-drift embers bank low and rise; halted embers hang high and fall.
  const topPct =
    field.drift === 'down' ? 8 + ((index * 29) % 45) : 68 + ((index * 17) % 26);
  const sizePx = 2 + (index % 3); // 2–4px square embers
  const durationS =
    field.drift === 'up-fast'
      ? 2.4 + (index % 5) * 0.35
      : field.drift === 'down'
        ? 4.5 + (index % 4) * 0.5
        : 8 + (index % 4) * 1.2;
  const delayS = (index * 0.7) % durationS;
  return {
    position: 'absolute',
    left: `${leftPct}%`,
    top: `${topPct}%`,
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    borderRadius: '9999px',
    // House tokens only — brand orange body; the ONE house red when halted.
    backgroundColor:
      field.drift === 'down' ? 'var(--color-blocker)' : 'var(--color-brand)',
    animationName: DRIFT_ANIMATION[field.drift],
    animationDuration: `${durationS}s`,
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    animationDelay: `${delayS}s`,
  };
}

interface EmberFieldProps {
  mood: FleetMood;
}

export default function EmberField({ mood }: EmberFieldProps) {
  const reduced = useReducedMotionPref();

  // Guard FIRST — before any keyframe or DOM node is computed/emitted.
  if (reduced) return null;

  const field = MOOD_FIELD[mood];
  const count = Math.min(field.count, MAX_EMBERS); // never exceed 20
  const halted = field.drift === 'down';

  return (
    <div
      data-testid="ember-field"
      data-mood={mood}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 hidden overflow-hidden lg:block"
    >
      <style>{EMBER_KEYFRAMES}</style>
      {/* Relative container: every ember is absolutely positioned inside it,
          so particle state changes never touch layout. */}
      <div className="relative h-full w-full">
        {Array.from({ length: count }, (_, i) => (
          <span
            key={i}
            data-testid="ember-field-ember"
            data-drift={field.drift}
            className={
              `ef-ember ${DRIFT_CLASS[field.drift]}` +
              (halted ? ' ef-ember--halted' : '')
            }
            style={emberStyle(i, field)}
          />
        ))}
      </div>
    </div>
  );
}
